import { useMemo, useState } from "react";

export function useSortFilter(rows, opts = {}) {
  const { searchKeys = [], defaultSort = null } = opts;
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(defaultSort); // { key, dir: 'asc'|'desc' }

  const toggleSort = (key) => {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return null; // third click clears
    });
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((r) =>
        searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(needle))
      );
    }
    if (sort) {
      const { key, dir } = sort;
      out = [...out].sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const na = Number(av);
        const nb = Number(bv);
        const bothNum = !isNaN(na) && !isNaN(nb) && typeof av !== "string" && typeof bv !== "string";
        let cmp;
        if (bothNum || (!isNaN(na) && !isNaN(nb) && String(av).match(/^-?\d/) && String(bv).match(/^-?\d/))) {
          cmp = na - nb;
        } else {
          cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
        }
        return dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, q, sort, searchKeys]);

  return { q, setQ, sort, toggleSort, rows: filtered };
}

export function SortableTh({ label, sortKey, sort, toggleSort, className = "" }) {
  const active = sort?.key === sortKey;
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "";
  return (
    <th
      onClick={() => toggleSort(sortKey)}
      className={`cursor-pointer select-none hover:bg-secondary ${className}`}
      data-testid={`sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[9px] w-3 inline-block ${active ? "" : "opacity-30"}`}>{arrow || "↕"}</span>
      </span>
    </th>
  );
}

export function SearchBox({ value, onChange, placeholder = "Search…", testid = "table-search" }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="h-8 px-3 bg-white border border-border font-mono text-xs w-48 focus:border-foreground focus:outline-none"
    />
  );
}
